import { useEffect, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';

// Account Session component names. This ticket mounts account onboarding and
// nothing else; SC-R7-DASH-03 passes its own set (account_management,
// payouts, tax) to this same hook.
export const ACCOUNT_ONBOARDING_COMPONENTS = ['account_onboarding'];

// Pinned, not left to Stripe's default. The embedded components render in a
// cross-origin iframe and pick their language from the browser when no locale
// is passed — so a restaurant whose browser is set to Turkish got a Turkish
// Stripe form sitting inside an English-only dashboard, mid-sentence with our
// own copy. Thisway ships one language; the widget has to match it. Change
// this the day the app itself becomes multilingual, and change it here only —
// this is the single place the widget's language is decided.
export const LOCALE = 'en-US';

// DEPLOYMENT CONSTRAINT, easy to break from outside this file.
//
// Stripe authenticates the connected account in a popup to a Stripe-owned
// window — required here because Stripe collects this account's requirements
// (losses_collector=stripe), and Stripe states the popup cannot be designed
// away: "Some behavior in embedded components, such as user authentication,
// must be presented in a popup." Two ordinary hardening moves silently kill it:
//
//   * `Cross-Origin-Opener-Policy: same-origin` on the app's responses. Stripe
//     requires `unsafe-none` (the default, i.e. no header); any other value
//     "breaks user authentication in Connect embedded components".
//   * A Content-Security-Policy that does not allow Stripe. It needs
//     frame-src and script-src https://connect-js.stripe.com https://js.stripe.com,
//     img-src https://*.stripe.com, and the empty-style hash in style-src.
//
// Neither header is set today (checked on localhost and fe-test). The failure
// mode if one is added is a popup that never completes, so onboarding stalls
// with nothing in our own logs.

// The Finance palette (spec 2026-09-21 §3.1 / §5.6), so the Business
// Details strip, the Payouts list and Stripe's own components speak one
// language. Hex literals rather than the CSS variables the pages use:
// Stripe's iframe cannot read our stylesheet. The two objects mirror the
// :root / .dark blocks in src/index.css; change them together.
const LIGHT_VARIABLES = {
  colorPrimary: '#2f6bed', // fin.blue
  colorBackground: '#ffffff', // fin.surface
  colorText: '#0f1a2e', // fin.ink
  colorSecondaryText: '#6b7793', // fin.muted
  colorBorder: '#e6e9f0', // fin.border
  colorDanger: '#b42318', // fin.red
  buttonPrimaryColorBackground: '#2f6bed',
  buttonPrimaryColorBorder: '#2f6bed',
  buttonPrimaryColorText: '#ffffff',
  buttonSecondaryColorBackground: '#ffffff',
  buttonSecondaryColorBorder: '#d9dfea', // fin.border-2
  buttonSecondaryColorText: '#0f1a2e',
  badgeSuccessColorBackground: '#e3f5ea', // fin.green-pale
  badgeSuccessColorBorder: '#e3f5ea',
  badgeSuccessColorText: '#15803d', // fin.green
  badgeWarningColorBackground: '#fdf3d8', // fin.amber-pale
  badgeWarningColorBorder: '#fdf3d8',
  badgeWarningColorText: '#8a5a00', // fin.amber
  badgeDangerColorBackground: '#fde8e8', // fin.red-pale
  badgeDangerColorBorder: '#fde8e8',
  badgeDangerColorText: '#b42318',
  badgeNeutralColorBackground: '#eef1f6', // fin.neutral-pale
  badgeNeutralColorBorder: '#eef1f6',
  badgeNeutralColorText: '#3c4a68', // fin.neutral
  formAccentColor: '#2f6bed',
  formHighlightColorBorder: '#2f6bed',
  offsetBackgroundColor: '#fafbfd', // fin.recess
};

const DARK_VARIABLES = {
  colorPrimary: '#6b9cff',
  colorBackground: '#1a2130',
  colorText: '#e8ecf3',
  colorSecondaryText: '#8f9bb3',
  colorBorder: '#2a3345',
  colorDanger: '#f87171',
  buttonPrimaryColorBackground: '#6b9cff',
  buttonPrimaryColorBorder: '#6b9cff',
  buttonPrimaryColorText: '#ffffff',
  buttonSecondaryColorBackground: '#1a2130',
  buttonSecondaryColorBorder: '#354057',
  buttonSecondaryColorText: '#e8ecf3',
  badgeSuccessColorBackground: '#0f2a1b',
  badgeSuccessColorBorder: '#0f2a1b',
  badgeSuccessColorText: '#4ade80',
  badgeWarningColorBackground: '#3a2b0a',
  badgeWarningColorBorder: '#3a2b0a',
  badgeWarningColorText: '#f5c451',
  badgeDangerColorBackground: '#3b1414',
  badgeDangerColorBorder: '#3b1414',
  badgeDangerColorText: '#f87171',
  badgeNeutralColorBackground: '#232b3b',
  badgeNeutralColorBorder: '#232b3b',
  badgeNeutralColorText: '#c3cbd9',
  formAccentColor: '#6b9cff',
  formHighlightColorBorder: '#6b9cff',
  offsetBackgroundColor: '#151b28',
};

export const appearanceFor = (isDark) => ({
  overlays: 'dialog',
  variables: {
    fontFamily: 'Lato, ui-sans-serif, system-ui, sans-serif',
    // The app's own text-sm body copy size, not Stripe's default.
    fontSizeBase: '14px',
    // The base unit Stripe scales every other spacing value from — pixel
    // values only, 8-20px. Stripe's own words: "Increase or decrease this
    // value to make your layout more or less spacious."
    //
    // 14px. Not the 12px that matched connect-finance-tables.js's note bar
    // (`py-3`) and section-card.js's header row (`gap-3`). Those pages are
    // dense tables where 12px is right; the account pages are one panel on
    // an otherwise empty page, and at 12px the panel — its buttons included —
    // read as small and lost. This is the only lever for that: there is no
    // button-size variable, and padding is half of what makes a button look
    // pressable. Not 16px either: that padding also inflates the tall empty
    // frame Stripe draws around its signed-out prompt, which is the other
    // half of why these pages read as empty. 14 is the point where the
    // button is still bigger than it was and the frame has stopped growing.
    //
    // One appearance object serves every embedded component, so this moves
    // Bank Account, Payouts, Disputes, Tax Documents and onboarding too.
    spacingUnit: '14px',
    // Stripe's type scale, raised (owner, 2026-09-23). This used to stay at
    // the app's 14px throughout, on the argument that a Stripe panel set in
    // a different size from the page header above it reads as pasted in —
    // but the balances card on Payouts then read too small beside our own
    // figures. fontSizeBase above stays 14px; the heading, body and label
    // sizes Stripe draws with are set explicitly here instead. One
    // appearance serves every embedded component, so Business Details,
    // Disputes, Tax Documents and onboarding grow with Payouts — the owner
    // accepted that.
    headingXlFontSize: '32px',
    headingLgFontSize: '24px',
    headingMdFontSize: '20px',
    headingSmFontSize: '17px',
    headingXsFontSize: '15px',
    bodyMdFontSize: '16px',
    bodySmFontSize: '14px',
    labelMdFontSize: '15px',
    labelSmFontSize: '13px',
    borderRadius: '8px',
    // TWModal's backdrop (`bg-slate-900/60`) doesn't vary with the theme, so
    // neither does this.
    overlayBackdropColor: 'rgba(15, 23, 42, 0.6)', // slate-900/60
    ...(isDark ? DARK_VARIABLES : LIGHT_VARIABLES),
  },
});

// index.css's Google Fonts @import lives in the parent document and does not
// reach Stripe's iframe, so `fontFamily` above is a no-op unless Connect.js is
// handed the stylesheet itself.
const FONTS = [
  {
    cssSrc:
      'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap',
  },
];

// Turns an Account Session client secret into a Stripe Connect instance.
// `secretSource` is either shape Stripe's fetchClientSecret contract can be
// built from: the onboarding path still passes an already-minted string,
// because its click already fetched one — connect-onboarding.js's
// onClickContinueOnStripe is the one place that decides between
// StartOnboarding and ResendLink and already needs that same response's
// onboardingUrl for the Account Link fallback, so one fetch answers both
// questions rather than this hook racing its own call against that one. That
// string stays single-use below. The other surfaces (SC-R7-DASH-03) instead
// pass a function — `useAccountSession`'s `fetchSecret` — that this hook can
// call again itself whenever Stripe asks for a fresh secret. That function
// must be stable across renders, or Stripe would be handed a new
// fetchClientSecret (and this effect would re-run) on every parent render;
// `useAccountSession` returns a `useCallback` with an empty dependency list
// for exactly this reason.
//
// `secretSource` starts (and, whenever the caller has nothing minted, stays)
// null. That is the ordinary pre-action state, not an error — the caller
// renders its own Account Link button until a click hands this hook a
// secret, so `loading` has nothing to report while it waits.
//
// `components` is never sent to Stripe or to our server — the account
// session is onboarding-only regardless of what is asked for, a fact the
// caller cannot change. It is kept purely to key this effect, so two callers
// requesting different component sets (this screen's onboarding-only set vs.
// SC-R7-DASH-03's account_management/payouts/tax) are guaranteed separate
// instances rather than sharing one.
//
// Returns { instance, loading, error }; `error` is the caller's signal to
// fall back to the Account Link path. Building the instance from an
// already-minted secret is synchronous (`loadConnectAndInitialize` returns
// immediately and loads connect.js in the background), so `loading` never
// observably goes true here — the network wait happened before this hook
// ever saw a secret, and the caller's own button already renders that wait.
const useConnectInstance = (
  secretSource,
  components = ACCOUNT_ONBOARDING_COMPONENTS,
  isDark = false,
) => {
  const [state, setState] = useState({
    instance: null,
    loading: false,
    error: null,
  });
  const componentKey = (components || []).join(',');

  useEffect(() => {
    let active = true;

    if (!secretSource) {
      // Nothing minted yet — not an error, just nothing to do.
      setState({ instance: null, loading: false, error: null });
      return () => {
        active = false;
      };
    }

    // Trimmed before the emptiness check: a whitespace-only value (a stray
    // newline from an env file, say) is not a real key and must take the
    // link path rather than attempt a mount that can only fail.
    const publishableKey = (
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
    ).trim();
    if (!publishableKey) {
      // Nothing to mount against — the caller renders the link path rather
      // than an empty frame. In practice the caller already checks this same
      // env var before ever setting `secretSource`, so this branch is a
      // second, independent backstop rather than the primary guard.
      setState({
        instance: null,
        loading: false,
        error: new Error('Stripe publishable key is not configured.'),
      });
      return () => {
        active = false;
      };
    }

    // `secretSource` is either a string an earlier click already minted — the
    // onboarding path, where the secret is single-use and there is no route
    // this hook could call for another — or a function that mints one on
    // demand. Stripe calls this again when a session expires, so the function
    // form is what the surfaces in SC-R7-DASH-03 pass.
    let consumed = false;
    const fetchClientSecret = async () => {
      if (typeof secretSource === 'function') return secretSource();
      if (consumed) {
        throw new Error('This Stripe session has expired. Please try again.');
      }
      consumed = true;
      return secretSource;
    };

    try {
      const instance = loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret,
        locale: LOCALE,
        appearance: appearanceFor(isDark),
        fonts: FONTS,
      });
      if (active) setState({ instance, loading: false, error: null });
    } catch (error) {
      if (active) setState({ instance: null, loading: false, error });
    }

    return () => {
      active = false;
    };
    // `isDark` is deliberately absent: a theme flip must never re-create the
    // instance (and, now, never re-consumes the single-use secret). The
    // effect below re-skins the live instance instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretSource, componentKey]);

  useEffect(() => {
    if (!state.instance) return;
    state.instance.update({ appearance: appearanceFor(isDark) });
  }, [state.instance, isDark]);

  return state;
};

export default useConnectInstance;
